/**
 * PageLink — inline atom node for `[[Page Name]]` chips in a notebook page.
 *
 * Markdown form: `[[Page Name]]`.
 *
 * On parse, we look up the title in the workspace's page tree (passed as
 * an extension option) and stash the resolved `{ pageId, slug, icon }` on
 * the node's attrs. If no match is found, the chip renders as a broken
 * link (line-through + red dot) — see `PageEditor.tsx` for the styling.
 *
 * On serialize, we emit `[[Page Name]]` (the title is the canonical key;
 * pageId is in the attrs for navigation but the markdown stays readable).
 */

import { Node, mergeAttributes } from '@tiptap/core'
import type { Page } from '@friday/shared/realtime'

export interface PageLinkOptions {
  /** Workspace id — used by the chip's click handler to navigate. */
  wsId: string
  /**
   * Returns the latest page tree. The extension reads this lazily on
   * every parse, so callers can hold a ref instead of recreating the
   * extension when the tree changes (which would re-mount the editor
   * and kick the user out of edit mode).
   */
  getPageTree: () => Page[]
}

export interface PageLinkStorage {
  markdown: {
    serialize: (state: never, node: never) => void
    parse: {
      setup: (md: import('markdown-it').MarkdownIt) => void
      updateDOM: (element: HTMLElement) => void
    }
  }
}

const PAGE_LINK_TAG = 'a[data-page-link]'

export const PageLink = Node.create<PageLinkOptions, PageLinkStorage>({
  name: 'pageLink',
  inline: true,
  group: 'inline',
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { wsId: '', getPageTree: () => [] }
  },

  addAttributes() {
    return {
      pageId: { default: '' },
      slug: { default: '' },
      title: { default: '' },
      icon: { default: null },
      broken: { default: false },
    }
  },

  parseHTML() {
    return [{ tag: PAGE_LINK_TAG }]
  },

  renderHTML({ node, HTMLAttributes }) {
    const classes = ['page-link-chip']
    if (node.attrs.broken) classes.push('page-link-chip--broken')
    return [
      'a',
      mergeAttributes(HTMLAttributes, {
        'data-page-link': '',
        'data-page-id': node.attrs.pageId,
        'data-page-slug': node.attrs.slug,
        class: classes.join(' '),
        href: node.attrs.pageId ? `#/page/${node.attrs.pageId}` : '#',
      }),
      `${node.attrs.icon ?? '📄'} ${node.attrs.title || 'Untitled'}`,
    ]
  },

  addStorage() {
    const self = this
    return {
      markdown: {
        serialize(state: any, node: any) {
          state.write(`[[${node.attrs.title}]]`)
        },
        parse: {
          setup(md) {
            // Inline rule that finds `[[Title]]` (single-line, no newlines
            // inside the brackets). Runs before markdown-it's `emphasis`
            // rule so `[[hello]]` doesn't get half-eaten.
            md.inline.ruler.before('emphasis', 'page_link', (state: any, silent: boolean) => {
              const start = state.pos
              const src = state.src
              if (src.charCodeAt(start) !== 0x5b /* [ */) return false
              if (src.charCodeAt(start + 1) !== 0x5b) return false
              let pos = start + 2
              while (pos < src.length) {
                const c = src.charCodeAt(pos)
                if (c === 0x0a /* \n */) return false
                if (c === 0x5b /* [ */) {
                  // nested `[[` not allowed inside for v1
                  return false
                }
                if (c === 0x5d /* ] */ && src.charCodeAt(pos + 1) === 0x5d) {
                  break
                }
                pos++
              }
              if (pos >= src.length) return false
              const title = src.slice(start + 2, pos).trim()
              if (!title) return false
              if (!silent) {
                const open = state.push('page_link_open', '', 1)
                open.attrs = [['class', 'page-link-chip']]
                const text = state.push('text', '', 0)
                text.content = `📄 ${title}`
                state.push('page_link_close', '', -1)
                // Stash the title on the open token so the renderer can
                // pick it up. (markdown-it custom renderers receive the
                // token + all its attrs.)
                open.attrs.push(['data-page-link-title', title])
              }
              state.pos = pos + 2
              return true
            })

            // Renderer for the page_link tokens: produce a single
            // <a data-page-link> element with the raw title in an attr
            // so the updateDOM pass can resolve it against the page tree.
            md.renderer.rules.page_link_open = (tokens: any[], idx: number) => {
              const tok = tokens[idx]
              const titleAttr = (tok.attrs ?? []).find((a: [string, string]) => a[0] === 'data-page-link-title')
              const title = titleAttr ? titleAttr[1] : ''
              return `<a data-page-link data-page-link-title="${escapeAttr(title)}" href="#">📄 ${escapeHtml(title)}</a>`
            }
            md.renderer.rules.page_link_close = () => ''
            // Ensure the `text` token between open/close still gets rendered;
            // we want a single <a> only, so the renderer above swallows both
            // tokens and the parser pipeline doesn't need to render the text.
            // We achieve "no extra text" by emitting the title *inside* the
            // open token, so updateDOM is the one true source of truth.
          },
          updateDOM(element) {
            // The rendered HTML may contain <a data-page-link data-page-link-title="...">📄 Title</a>.
            // Resolve the title against the page tree and add data-page-id,
            // data-page-slug, and data-page-icon for parseHTML to pick up.
            const anchors = element.querySelectorAll('a[data-page-link]')
            const tree = self.options.getPageTree()
            for (const a of Array.from(anchors)) {
              const title = a.getAttribute('data-page-link-title') || ''
              const found = title ? findPageByTitle(tree, title) : null
              if (found) {
                a.setAttribute('data-page-id', found.id)
                a.setAttribute('data-page-slug', found.slug)
                if (found.icon) a.setAttribute('data-page-icon', found.icon)
                a.removeAttribute('data-page-link-title')
                a.setAttribute('href', `#/page/${found.id}`)
                a.setAttribute('data-page-link-resolved', 'true')
              } else {
                a.setAttribute('data-page-broken', 'true')
                a.removeAttribute('data-page-link-title')
              }
            }
          },
        },
      },
    }
  },
})

function findPageByTitle(pages: Page[], title: string): Page | null {
  // First pass: exact title match.
  for (const p of pages) {
    if (p.title === title && !p.archivedAt) return p
  }
  // Second pass: slug match (some authors will type the slug verbatim).
  for (const p of pages) {
    if (p.slug === title && !p.archivedAt) return p
  }
  return null
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function escapeAttr(s: string): string {
  return escapeHtml(s)
}
